import { STARTING_COINS, expansionCost, parcelBalance } from '../config/balancing';
import { purchaseCost } from '../config/catalog';
import { PARCELS, slotById } from '../config/map';
import { plotIdForLegacyCell, queryBuildableSubcell } from '../spatial/buildableArea';
import {
  hasMacroEntitlement,
  landOwnsPlot,
  plotById,
  starterPlotIds,
} from '../spatial/plots';
import { FOUNDATION_DIVISION, createSubgridSpec, legacyCellToSubgridOrigin } from '../spatial/subgrid';
import type { ActionResult, EntityKind, ParkState, PlacedEntity, RotationQuarter, SubcellAnchor } from '../types';
import {
  floorEraseRefundCoins,
  floorOverrideMap,
  floorPaintCoinCost,
  isExplicitFloorOverride,
  paintFloor as applyFloorPaint,
  resolvedFloorStyle,
  type FloorPaintStyle,
} from './floors';
import { canAfford, collectStoredIncome, maxLevel, revenuePerTick, upgradeCost } from './economy';
import { centerAnchorForSlotId, entityAnchor, entityRotation, evaluateOccupancy, slotByIdForAnchor } from './placement';
import { evaluateKioskPlacement } from './kioskCapacity';
import { wouldBreakIncomeReserve } from './startSafety';
import { evaluateStarterBuild, evaluateStarterCoinSpend } from './starterGate';
import { evaluateCatalogUnlock, evaluateParcelUnlock } from './unlocks';

const PLACEMENT_SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export { setEntityPalette } from './appearance';

export function createInitialState(now = Date.now()): ParkState {
  return {
    coins: STARTING_COINS,
    unlockedLandIds: starterPlotIds(),
    entities: [],
    builtKindMilestones: [],
    lastSaveTimestamp: now,
  };
}

export function isParcelUnlocked(state: ParkState, parcelId: string): boolean {
  if (plotById(parcelId)) {
    return landOwnsPlot(state.unlockedLandIds, parcelId);
  }
  return hasMacroEntitlement(state.unlockedLandIds, parcelId);
}

export function entityAtSlot(state: ParkState, slotId: string): PlacedEntity | undefined {
  return state.entities.find((entity) => entity.slotId === slotId);
}

export function entitiesAtSlot(state: ParkState, slotId: string): PlacedEntity[] {
  return state.entities.filter((entity) => entity.slotId === slotId);
}

function nextEntityId(state: ParkState, slotId: string): string {
  const base = `ent_${slotId}`;
  if (!state.entities.some((entity) => entity.id === base)) {
    return base;
  }
  let suffix = 2;
  while (state.entities.some((entity) => entity.id === `${base}_${suffix}`)) {
    suffix += 1;
  }
  return `${base}_${suffix}`;
}

function recordBuiltKindMilestone(state: ParkState, kind: EntityKind): void {
  state.builtKindMilestones ??= [];
  if (!state.builtKindMilestones.includes(kind)) {
    state.builtKindMilestones.push(kind);
  }
}

function firstFreeAnchor(
  state: ParkState,
  slotId: string,
  kind: EntityKind,
  rotation: RotationQuarter,
): SubcellAnchor | null {
  const center = centerAnchorForSlotId(slotId);
  if (evaluateOccupancy(state, kind, center, rotation).ok) {
    return center;
  }
  const slot = slotById(slotId);
  if (!slot) {
    return null;
  }
  const origin = legacyCellToSubgridOrigin(slot.col, slot.row, PLACEMENT_SPEC);
  for (let localX = 0; localX < FOUNDATION_DIVISION; localX += 1) {
    for (let localZ = 0; localZ < FOUNDATION_DIVISION; localZ += 1) {
      const anchor = { sx: origin.sx + localX, sz: origin.sz + localZ };
      if (evaluateOccupancy(state, kind, anchor, rotation).ok) {
        return anchor;
      }
    }
  }
  return null;
}

export function canUnlockExpansion(state: ParkState, parcelId: string): boolean {
  if (!evaluateStarterCoinSpend(state).ok) {
    return false;
  }
  const cost = expansionCost(parcelId);
  if (plotById(parcelId)) {
    return evaluateParcelUnlock(parcelId, state).ok && canAfford(state.coins, cost);
  }
  const spec = parcelBalance(parcelId);
  if (!spec) {
    return false;
  }
  return evaluateParcelUnlock(parcelId, state).ok && canAfford(state.coins, spec.cost);
}

export function rotateEntity(state: ParkState, entityId: string): ActionResult {
  const entity = state.entities.find((item) => item.id === entityId);
  if (!entity) {
    return { ok: false, reason: 'empty' };
  }
  const current = entityRotation(entity);
  const next = ((current + 90) % 360) as RotationQuarter;
  const occupancy = evaluateOccupancy(state, entity.kind, entityAnchor(entity), next, entity.id);
  if (!occupancy.ok) {
    return { ok: false, reason: occupancy.reason };
  }
  entity.rotation = next;
  return { ok: true };
}

export function moveEntity(
  state: ParkState,
  entityId: string,
  anchor: SubcellAnchor,
  rotation?: RotationQuarter,
): ActionResult {
  const entity = state.entities.find((item) => item.id === entityId);
  if (!entity) {
    return { ok: false, reason: 'empty' };
  }
  const nextRotation = rotation ?? entityRotation(entity);
  const occupancy = evaluateOccupancy(state, entity.kind, anchor, nextRotation, entity.id);
  if (!occupancy.ok) {
    return { ok: false, reason: occupancy.reason };
  }
  entity.anchor = { sx: anchor.sx, sz: anchor.sz };
  entity.rotation = nextRotation;
  entity.slotId = slotByIdForAnchor(anchor);
  return { ok: true };
}

export function placeEntityWithChargedCost(
  state: ParkState,
  slotId: string,
  kind: EntityKind,
  chargedCost: number,
  options?: { anchor?: SubcellAnchor; rotation?: RotationQuarter },
): ActionResult {
  const slot = slotById(slotId);
  if (!slot && !options?.anchor) {
    return { ok: false, reason: 'unknown_slot' };
  }
  if (slot && !options?.anchor && !isParcelUnlocked(state, plotIdForLegacyCell(slot.col, slot.row))) {
    return { ok: false, reason: 'locked' };
  }
  const kioskGate = evaluateKioskPlacement(state, kind);
  if (!kioskGate.ok) {
    return kioskGate;
  }
  const starterBuild = evaluateStarterBuild(state, kind);
  if (!starterBuild.ok) {
    return starterBuild;
  }
  if (!evaluateCatalogUnlock(kind, state).ok) {
    return { ok: false, reason: 'locked_item' };
  }
  const rotation = options?.rotation ?? 0;
  const anchor = options?.anchor ?? (slot ? firstFreeAnchor(state, slotId, kind, rotation) : null);
  if (!anchor) {
    return { ok: false, reason: 'occupied' };
  }
  const query = queryBuildableSubcell(anchor, state.unlockedLandIds);
  if (!query.buildable) {
    return { ok: false, reason: 'locked' };
  }
  const occupancy = evaluateOccupancy(state, kind, anchor, rotation);
  if (!occupancy.ok) {
    return { ok: false, reason: occupancy.reason };
  }
  if (!canAfford(state.coins, chargedCost)) {
    return { ok: false, reason: 'cannot_afford' };
  }
  if (wouldBreakIncomeReserve(state, kind)) {
    return { ok: false, reason: 'income_reserve' };
  }
  const resolvedSlotId = slotByIdForAnchor(anchor);
  state.coins -= chargedCost;
  state.entities.push({
    id: nextEntityId(state, resolvedSlotId),
    kind,
    slotId: resolvedSlotId,
    level: 1,
    storedCoins: 0,
    anchor,
    rotation,
  });
  recordBuiltKindMilestone(state, kind);
  return { ok: true };
}

export function placeEntity(
  state: ParkState,
  slotId: string,
  kind: EntityKind,
  options?: { anchor?: SubcellAnchor; rotation?: RotationQuarter },
): ActionResult {
  return placeEntityWithChargedCost(state, slotId, kind, purchaseCost(kind), options);
}

export function upgradeEntity(state: ParkState, entityId: string): ActionResult {
  const entity = state.entities.find((item) => item.id === entityId);
  if (!entity) {
    return { ok: false, reason: 'empty' };
  }
  const cost = upgradeCost(entity.kind, entity.level);
  if (cost === null) {
    return { ok: false, reason: entity.level >= maxLevel(entity.kind) ? 'max_level' : 'not_upgradable' };
  }
  if (!canAfford(state.coins, cost)) {
    return { ok: false, reason: 'cannot_afford' };
  }
  state.coins -= cost;
  entity.level += 1;
  return { ok: true };
}

export function collectEntity(state: ParkState, entityId: string): ActionResult & { amount?: number } {
  const entity = state.entities.find((item) => item.id === entityId);
  if (!entity) {
    return { ok: false, reason: 'empty' };
  }
  if (revenuePerTick(entity) <= 0 && entity.storedCoins <= 0) {
    return { ok: false, reason: 'no_income' };
  }
  const amount = collectStoredIncome(entity);
  state.coins += amount;
  return { ok: true, amount };
}

/**
 * Remove a placed entity. Refunds exactly the catalog BASE BUILD COST.
 * Upgrade spend and leftover stored income are not refunded.
 */
export function removeEntity(state: ParkState, entityId: string): ActionResult & { refund?: number } {
  const index = state.entities.findIndex((item) => item.id === entityId);
  if (index < 0) {
    return { ok: false, reason: 'empty' };
  }
  const [entity] = state.entities.splice(index, 1);
  const refund = purchaseCost(entity.kind);
  state.coins += refund;
  return { ok: true, refund };
}

export function paintFloorCell(state: ParkState, coord: SubcellAnchor, style: FloorPaintStyle): ActionResult {
  const before = resolvedFloorStyle(coord, floorOverrideMap(state));
  const cost = floorPaintCoinCost(before, style);
  const refund = floorEraseRefundCoins(coord, before, style, isExplicitFloorOverride(state, coord));
  if (cost > 0) {
    const starterSpend = evaluateStarterCoinSpend(state);
    if (!starterSpend.ok) {
      return starterSpend;
    }
  }
  if (cost > 0 && !canAfford(state.coins, cost)) {
    return { ok: false, reason: 'cannot_afford' };
  }
  const result = applyFloorPaint(state, coord, style);
  if (!result.ok) {
    return { ok: false, reason: result.reason };
  }
  if (cost > 0) {
    state.coins -= cost;
  }
  if (refund > 0) {
    state.coins += refund;
  }
  return { ok: true };
}

export function expandLandWithChargedCost(
  state: ParkState,
  parcelId: string,
  chargedCost: number,
): ActionResult {
  const starterSpend = evaluateStarterCoinSpend(state);
  if (!starterSpend.ok) {
    return starterSpend;
  }
  const plot = plotById(parcelId);
  if (plot) {
    if (landOwnsPlot(state.unlockedLandIds, parcelId)) {
      return { ok: false, reason: 'already_unlocked' };
    }
    const gate = evaluateParcelUnlock(parcelId, state);
    if (!gate.ok) {
      return { ok: false, reason: 'parcel_prereq' };
    }
    if (!canAfford(state.coins, chargedCost)) {
      return { ok: false, reason: 'cannot_afford' };
    }
    state.coins -= chargedCost;
    state.unlockedLandIds.push(parcelId);
    return { ok: true };
  }
  const parcel = PARCELS.find((item) => item.id === parcelId);
  if (!parcel) {
    return { ok: false, reason: 'unknown_parcel' };
  }
  if (isParcelUnlocked(state, parcelId)) {
    return { ok: false, reason: 'already_unlocked' };
  }
  const gate = evaluateParcelUnlock(parcelId, state);
  if (!gate.ok) {
    return { ok: false, reason: 'parcel_prereq' };
  }
  if (!canAfford(state.coins, chargedCost)) {
    return { ok: false, reason: 'cannot_afford' };
  }
  state.coins -= chargedCost;
  state.unlockedLandIds.push(parcelId);
  return { ok: true };
}

export function expandLand(state: ParkState, parcelId: string): ActionResult {
  return expandLandWithChargedCost(state, parcelId, expansionCost(parcelId));
}
